import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { FileSignature, User as UserIcon, LogOut, ShieldCheck, LogIn, TrendingUp, Bot, Home as HomeIcon, Trophy, HelpCircle } from "lucide-react";
import LangSwitcher from "@/components/LangSwitcher";
import Avatar from "@/components/Avatar";
import NotificationsBell from "@/components/NotificationsBell";
import NetworkStatusWidget from "@/components/NetworkStatusWidget";
import WeatherWidget from "@/components/WeatherWidget";
import SupportCenter from "@/components/SupportCenter";
import { useUser } from "@/context/UserContext";
import { useI18n } from "@/i18n/I18nContext";
import { isProfileIncomplete } from "@/lib/profileStatus";
const Logo = ({ hideOnMobile }) => (
  <NavLink to="/" className={`items-center gap-2.5 transition-transform hover:scale-[1.02] shrink-0 ${hideOnMobile ? 'hidden lg:flex' : 'flex'}`} data-testid="navbar-logo">
    <img
      src="/logo_nav.webp"
      alt="Colygon Mobility"
      data-testid="navbar-colygon-logo"
      className="h-9 md:h-10 w-auto object-contain shrink-0"
    />
  </NavLink>
);


const NavBar = () => {
  const { user, logout } = useUser();
  const { t } = useI18n();
  const location = useLocation();
  const isLogin = location.pathname === "/login";
  const [loggingOut, setLoggingOut] = useState(false);
  const isHr = user?.role === "hr_manager" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const roleShort = isSuperAdmin ? "SUPER" : user?.role === "hr_manager" ? "HR" : null;
  const showTools = user && !isLogin;

  // Ayuda en todas las pantallas (antes solo en el inicio, dentro del
  // saludo). /?help=requests — el aviso de «Colygon ha respondido» — la abre
  // en «Mis consultas»; el parámetro se quita para que no se reabra.
  const [searchParams, setSearchParams] = useSearchParams();
  const helpParam = searchParams.get("help");
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpView, setHelpView] = useState("new");
  useEffect(() => {
    if (helpParam !== "requests" || !user) return;
    setHelpView("mine");
    setHelpOpen(true);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("help");
      return next;
    }, { replace: true });
  }, [helpParam, user, setSearchParams]);

  // Entre tablet y portátil pequeño (sm–xl) solo iconos, con el nombre para
  // lectores de pantalla y como tooltip: con los textos, la barra no cabía y
  // se desplazaba de lado (super admin: 6 secciones en ~520 px).
  const linkClass = ({ isActive }) =>
    `relative inline-flex items-center gap-2 px-3 xl:px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
      isActive
        ? "bg-white/[0.12] text-white ring-1 ring-inset ring-white/15 after:absolute after:left-1/2 after:-translate-x-1/2 after:-bottom-[7px] after:h-[3px] after:w-6 after:rounded-full after:bg-colygon-light"
        : "text-white/70 hover:text-white hover:bg-white/[0.08]"
    }`;

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    setLoggingOut(false);
  };

  return (
    <>
      <header className={isLogin ? "absolute w-full z-50 bg-transparent pt-4" : "bg-colygon-darkest/95 sticky top-0 z-20 backdrop-blur-xl supports-[backdrop-filter]:bg-colygon-darkest/85 border-b border-white/10 shadow-[0_8px_24px_-12px_rgba(0,59,23,0.55)]"}>
      <div className="w-full max-w-[1600px] mx-auto px-3 md:px-6 py-2.5 flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-6 shrink-0 sm:shrink">
          {/* En móvil la navegación vive en la barra inferior (BottomNav):
              aquí solo quedan logo, idioma, campana y salir — y el logo
              vuelve a verse también para RRHH. */}
          {!isLogin && <Logo />}
          {user && (
            <nav className="hidden sm:flex flex-1 items-center gap-2 no-scrollbar" data-testid="main-nav">
              {user.role === "pmst_guest" ? (
                <NavLink to="/plan" className={linkClass} data-testid="nav-plan" title={t("nav.plan")}>
                  <FileSignature className="h-4 w-4" />
                  <span className="sr-only xl:not-sr-only">{t("nav.plan")}</span>
                </NavLink>
              ) : (<>
              <NavLink to="/" end className={linkClass} data-testid="nav-register-route" title={t("nav.home")}>
                <HomeIcon className="h-4 w-4" />
                <span className="sr-only xl:not-sr-only">{t("nav.home")}</span>
              </NavLink>
              <NavLink to="/profile" className={linkClass} data-testid="nav-profile" title={t("nav.profile")}>
                <span className="relative">
                  <UserIcon className="h-4 w-4" />
                  {/* Falta el domicilio o el DNI: lo mismo que avisa el perfil */}
                  {isProfileIncomplete(user) && (
                    <span data-testid="nav-profile-dot" className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-400" aria-hidden="true" />
                  )}
                </span>
                <span className="sr-only xl:not-sr-only">{t("nav.profile")}</span>
                {isProfileIncomplete(user) && <span className="sr-only">{t("nav.profile_incomplete")}</span>}
              </NavLink>
              {/* Sin ranking si la empresa ha desactivado la gamificación */}
              {user?.is_gamification_enabled !== false && (
                <NavLink to="/ranking" className={linkClass} data-testid="nav-ranking" title={t("nav.ranking")}>
                  <Trophy className="h-4 w-4" />
                  <span className="sr-only xl:not-sr-only">{t("nav.ranking")}</span>
                </NavLink>
              )}
              {isHr && (
                <NavLink to="/advisor" className={linkClass} data-testid="nav-advisor" title={t("nav.advisor")}>
                  <Bot className="h-4 w-4" />
                  <span className="sr-only xl:not-sr-only">{t("nav.advisor")}</span>
                </NavLink>
              )}
              {isHr && (
                <>
                  <NavLink to="/admin" className={linkClass} data-testid="nav-admin" title={isSuperAdmin ? t("nav.admin_super") : t("nav.admin_hr")}>
                    <ShieldCheck className="h-4 w-4" />
                    <span className="sr-only xl:not-sr-only">{isSuperAdmin ? t("nav.admin_super") : t("nav.admin_hr")}</span>
                  </NavLink>
                </>
              )}
              {isSuperAdmin && (
                <NavLink to="/super/analytics" className={linkClass} data-testid="nav-super-analytics" title={t("nav.super_analytics")}>
                  <TrendingUp className="h-4 w-4" />
                  <span className="sr-only xl:not-sr-only">{t("nav.super_analytics")}</span>
                </NavLink>
              )}
              </>)}
            </nav>
          )}
        </div>

        <div className="flex items-center gap-0.5 sm:gap-2 min-w-0">
          {/* Utilidades (antes dentro del saludo del inicio): conexión solo si
              falla, tiempo, las de la página (fondo del inicio) y ayuda */}
          {showTools && <NetworkStatusWidget variant="bar" />}
          {/* El tiempo pide la ubicación: solo en el inicio, como antes */}
          {showTools && location.pathname === "/" && <WeatherWidget variant="bar" />}
          {showTools && <div id="navbar-page-actions" className="flex items-center" />}
          {showTools && (
            <button type="button" onClick={() => { setHelpView("new"); setHelpOpen(true); }}
              data-testid="navbar-help" title={t("home.support_btn")} aria-label={t("home.support_btn")}
              className="p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/[0.08] transition-colors">
              <HelpCircle className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
          {showTools && <LangSwitcher variant="dark" />}
          {user && <NotificationsBell />}
          {user ? (
            <>
              <div
                className="hidden sm:flex items-center gap-2.5 pr-3 pl-1 py-1 rounded-full bg-white/[0.06] ring-1 ring-inset ring-white/15"
                data-testid="user-chip"
              >
                <Avatar initials={user.initials} name={user.name} photoUrl={user.photo_url} size="sm" />
                <div className="leading-tight">
                  <div className="text-sm font-medium text-white flex items-center gap-1.5">
                    {user.name}
                    {roleShort && (
                      <span className="text-[0.6875rem] px-1.5 py-0.5 rounded-md bg-colygon-light text-colygon-darkest font-bold tracking-wide">{roleShort}</span>
                    )}
                  </div>
                  <div className="text-[0.75rem] text-white/85">{user.email}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                disabled={loggingOut}
                data-testid="logout-button"
                title={t("nav.logout")}
                className="p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/[0.08] transition-colors"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : !isLogin && (
            <NavLink
              to="/login"
              data-testid="nav-login"
              className="inline-flex items-center gap-2 bg-[#15803D] hover:bg-[#166534] text-white text-sm px-4 py-2 rounded-xl font-medium shadow-sm"
            >
              <LogIn className="h-4 w-4" /> {t("nav.login")}
            </NavLink>
          )}
        </div>
      </div>
    </header>
    {user && <SupportCenter isOpen={helpOpen} initialView={helpView} onClose={() => setHelpOpen(false)} />}
    </>
  );
};

export default NavBar;
